import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DataUsageIcon from '@mui/icons-material/DataUsage';

const EstadisticasTrama = ({ estadisticas, sensor }) => {
  if (!estadisticas) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No hay estadísticas de trama disponibles
        </Typography>
      </Paper>
    );
  }

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatearValor = (valor) => {
    if (valor === null || valor === undefined) return 'N/A';
    return typeof valor === 'number' ? valor.toFixed(2) : valor;
  };

  const unidades = sensor?.unidades || '';

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DataUsageIcon />
        Estadísticas de Datos de Trama
      </Typography>
      
      <Grid container spacing={3}>
        {/* Información general */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Información General
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total de puntos:</Typography>
                <Chip label={estadisticas.totalDatos} size="small" color="primary" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Última lectura:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="caption">
                    {formatearFecha(estadisticas.ultimaFecha)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Valores estadísticos */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Valores Actuales
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Último valor:</Typography>
                <Chip 
                  label={`${formatearValor(estadisticas.ultimoValor)} ${unidades}`}
                  size="small" 
                  color="info"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Promedio:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatearValor(estadisticas.promedio)} {unidades}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Rangos de valores */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Rango de Valores en Trama
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingDownIcon color="error" fontSize="small" />
              <Typography variant="body2">
                Mínimo: <strong>{formatearValor(estadisticas.valorMinimo)} {unidades}</strong>
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="success" fontSize="small" />
              <Typography variant="body2">
                Máximo: <strong>{formatearValor(estadisticas.valorMaximo)} {unidades}</strong>
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

EstadisticasTrama.propTypes = {
  estadisticas: PropTypes.shape({
    totalDatos: PropTypes.number,
    ultimoValor: PropTypes.number,
    ultimaFecha: PropTypes.string,
    valorMinimo: PropTypes.number,
    valorMaximo: PropTypes.number,
    promedio: PropTypes.number
  }),
  sensor: PropTypes.shape({
    unidades: PropTypes.string,
    nombre: PropTypes.string
  })
};

export default EstadisticasTrama;